"use client";

import { c as _c } from "react/compiler-runtime";
import { Highlight, themes } from "prism-react-renderer";
import type React from "react";
import { createContext, use, useEffect, useRef, useState } from "react";
import { FiBarChart2, FiCheck, FiChevronRight, FiCode, FiCpu, FiDatabase, FiFileText, FiRefreshCw, FiSearch, FiTool, FiXCircle } from "react-icons/fi";
import { RiSparklingLine } from "react-icons/ri";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ScrollShadow } from "@/components/ui/scroll-shadow";
import { cx } from "@/lib/tailwind-utils";
import { AgentExecutionStatus } from "@/types/agent-execution";
import styles from "./agent-activity-log.module.css";
import { useThemeStore } from "@/stores/theme-store";
export type AgentActivityStatus = AgentExecutionStatus;
export enum AgentActivityKind {
  TOOL = "TOOL",
  FILE = "FILE",
  THINKING = "THINKING",
  PROGRESS = "PROGRESS",
  CODE = "CODE",
  SEARCH = "SEARCH",
  DATABASE = "DATABASE",
  CHART = "CHART",
  PLAN = "PLAN",
  RETRY = "RETRY",
}
export interface AgentActivityLogDetail {
  label: string;
  content: string;
  language?: "text" | "sql" | "python" | "javascript" | "json";
}
export interface AgentActivityLogItem {
  id: string;
  label: string;
  status: AgentActivityStatus;
  kind?: AgentActivityKind;
  detail?: AgentActivityLogDetail;
  details?: AgentActivityLogDetail[];
  elapsedMs?: number;
}
interface AgentActivityLogProps {
  items: AgentActivityLogItem[];
  defaultCollapsed?: boolean;
  className?: string;
}
interface AgentActivityTimelineProps {
  status: AgentActivityStatus;
  items: AgentActivityLogItem[];
  className?: string;
}
interface AgentActivityGroupProps {
  /** Later tool groups or assistant prose retire a singleton's emphasis. */
  hasFollowingActivity?: boolean;
  label: string;
  status: AgentActivityStatus;
  items: AgentActivityLogItem[];
  className?: string;
}
interface AgentActivityLogRootProps {
  children: React.ReactNode;
  className?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
interface AgentActivityLogHeaderProps {
  items: AgentActivityLogItem[];
  status: AgentActivityStatus;
  label?: string;
}
interface AgentActivityLogListProps {
  children: React.ReactNode;
  endRef?: React.RefObject<HTMLDivElement | null>;
  listRef?: React.RefObject<HTMLDivElement | null>;
  active?: boolean;
  indented?: boolean;
}
type AgentActivityPhase = "current" | "past";
const AgentActivityStretchContext = createContext<AgentActivityPhase | null>(null);
interface AgentActivityLogRowProps {
  item: AgentActivityLogItem;
  compact?: boolean;
  phase?: AgentActivityPhase;
}
const ACTIVITY_ACCORDION_VALUE = "activity";
const DETAIL_ACCORDION_VALUE = "detail";
const activitySummaryClassName = "flex w-full items-center justify-start gap-1.5 py-1 text-left font-normal leading-normal";
const accordionItemClassName = "border-none [&>h3]:my-0 [&>h3]:text-[length:inherit] [&>h3]:font-normal [&>h3]:leading-none";
const KIND_ICON_MAP: Record<AgentActivityKind, React.ElementType<{
  className?: string;
}>> = {
  [AgentActivityKind.TOOL]: FiTool,
  [AgentActivityKind.FILE]: FiFileText,
  [AgentActivityKind.THINKING]: RiSparklingLine,
  [AgentActivityKind.PROGRESS]: FiFileText,
  [AgentActivityKind.CODE]: FiCpu,
  [AgentActivityKind.SEARCH]: FiSearch,
  [AgentActivityKind.DATABASE]: FiDatabase,
  [AgentActivityKind.CHART]: FiBarChart2,
  [AgentActivityKind.PLAN]: FiFileText,
  [AgentActivityKind.RETRY]: FiRefreshCw
};
function getKindIcon(kind: AgentActivityKind | undefined) {
  return kind ? KIND_ICON_MAP[kind] : FiDatabase;
}
function getDetailIcon(language: AgentActivityLogDetail["language"]) {
  if (language === "python") return FiCpu;
  if (language === "sql" || language === "javascript" || language === "json") return FiCode;
  return FiFileText;
}
function scrollToBottom(element: HTMLDivElement) {
  element.scrollTop = element.scrollHeight;
}
function accordionChevronClassName(sizeClassName: string) {
  return cx(sizeClassName, "shrink-0 text-foreground-muted transition-transform duration-200 motion-reduce:transition-none group-data-[state=open]:rotate-90");
}
function AgentActivityLogRoot(t0) {
  const $ = _c(11);
  const {
    children,
    className,
    open,
    onOpenChange
  } = t0;
  const t1 = open ? ACTIVITY_ACCORDION_VALUE : "";
  let t2;
  if ($[0] !== onOpenChange) {
    t2 = value => onOpenChange(value === ACTIVITY_ACCORDION_VALUE);
    $[0] = onOpenChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== className) {
    t3 = cx("mt-1", styles.activity, className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children) {
    t4 = <AccordionItem value={ACTIVITY_ACCORDION_VALUE} className={accordionItemClassName}>{children}</AccordionItem>;
    $[4] = children;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <Accordion type="single" collapsible={true} value={t1} onValueChange={t2} className={t3}>{t4}</Accordion>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function AgentActivityLogHeader(t0) {
  const $ = _c(20);
  const {
    items,
    status,
    label: t1
  } = t0;
  const label = t1 === undefined ? "Activity" : t1;
  let t2;
  if ($[0] !== items) {
    t2 = items.filter(_temp);
    $[0] = items;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const running = t2;
  const isWorking = status === AgentExecutionStatus.RUNNING || status === AgentExecutionStatus.PENDING;
  let t3;
  if ($[2] !== items) {
    t3 = items.at(-1);
    $[2] = items;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const latest = t3;
  const activeLabel = latest?.label ?? label;
  const t4 = latest?.id ?? ACTIVITY_ACCORDION_VALUE;
  const t5 = latest?.kind;
  let t6;
  if ($[4] !== activeLabel || $[5] !== status || $[6] !== t4 || $[7] !== t5) {
    t6 = {
      id: t4,
      label: activeLabel,
      status,
      kind: t5
    };
    $[4] = activeLabel;
    $[5] = status;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isWorking || $[10] !== running) {
    t7 = isWorking && running.length > 1 ? <span className="ml-1.5 text-foreground-muted">+{running.length - 1} running</span> : null;
    $[9] = isWorking;
    $[10] = running;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== isWorking || $[13] !== items.length) {
    t8 = !isWorking ? <span className="ml-1.5 whitespace-nowrap text-foreground-muted">{`${items.length} step${items.length !== 1 ? "s" : ""}`}</span> : null;
    $[12] = isWorking;
    $[13] = items.length;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== activeLabel || $[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
    t9 = <AgentActivitySummary.Trigger item={t6} aria-label={activeLabel} aria-live="polite" aria-atomic="true" className="text-caption">{t7}{t8}</AgentActivitySummary.Trigger>;
    $[15] = activeLabel;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
function _temp(item) {
  return item.status === AgentExecutionStatus.RUNNING;
}
function AgentActivityLogList(t0) {
  const $ = _c(25);
  const {
    children,
    endRef,
    listRef,
    active: t1,
    indented: t2
  } = t0;
  const active = t1 === undefined ? true : t1;
  const indented = t2 === undefined ? true : t2;
  const contentRef = useRef(null);
  const isNearBottomRef = useRef(true);
  let t3;
  if ($[0] !== listRef) {
    t3 = () => {
      const list = listRef?.current;
      if (!list) {
        return;
      }
      isNearBottomRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < 32;
    };
    $[0] = listRef;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const handleScroll = t3;
  let t4;
  let t5;
  if ($[2] !== active || $[3] !== listRef) {
    t4 = () => {
      const content = contentRef.current;
      if (!active || !content || !listRef?.current || typeof ResizeObserver === "undefined") {
        return;
      }
      let animationFrame = null;
      const resizeObserver = new ResizeObserver(() => {
        if (!isNearBottomRef.current) {
          return;
        }
        if (animationFrame !== null) {
          cancelAnimationFrame(animationFrame);
        }
        animationFrame = requestAnimationFrame(() => {
          const list_0 = listRef.current;
          if (list_0) {
            scrollToBottom(list_0);
          }
          animationFrame = null;
        });
      });
      resizeObserver.observe(content);
      return () => {
        resizeObserver.disconnect();
        if (animationFrame !== null) {
          cancelAnimationFrame(animationFrame);
        }
      };
    };
    t5 = [active, listRef];
    $[2] = active;
    $[3] = listRef;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  const t6 = indented ? "ml-1.5 border-l border-border/50" : "ml-0 border-l-0";
  let t7;
  if ($[6] !== t6) {
    t7 = cx("rounded-none", styles.list, t6);
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = indented ? "pl-4" : "pl-0";
  let t9;
  if ($[8] !== t8) {
    t9 = cx("max-h-64 pt-0.5 scrollbar-thin", styles.list, t8);
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== endRef) {
    t10 = <div ref={endRef} />;
    $[10] = endRef;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== children || $[13] !== t10) {
    t11 = <div ref={contentRef} className="space-y-0">{children}{t10}</div>;
    $[12] = children;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== handleScroll || $[16] !== listRef || $[17] !== t11 || $[18] !== t9) {
    t12 = <ScrollShadow.Viewport ref={listRef} onScroll={handleScroll} className={t9}>{t11}</ScrollShadow.Viewport>;
    $[15] = handleScroll;
    $[16] = listRef;
    $[17] = t11;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ScrollShadow.Top />;
    t14 = <ScrollShadow.Bottom />;
    $[20] = t13;
    $[21] = t14;
  } else {
    t13 = $[20];
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t12 || $[23] !== t7) {
    t15 = <AccordionContent className="pb-0 text-inherit"><ScrollShadow.Root className={t7}>{t12}{t13}{t14}</ScrollShadow.Root></AccordionContent>;
    $[22] = t12;
    $[23] = t7;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  return t15;
}
function AgentActivityLogStatusIcon(t0) {
  const $ = _c(25);
  const {
    status,
    className,
    kind
  } = t0;
  let t1;
  if ($[0] !== kind) {
    t1 = getKindIcon(kind);
    $[0] = kind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const Icon = t1;
  if (status === AgentExecutionStatus.COMPLETED) {
    let t2;
    if ($[2] !== className) {
      t2 = cx("shrink-0 text-status-success-text", className);
      $[2] = className;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t2) {
      t3 = <FiCheck aria-label="Completed" className={t2} />;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  if (status === AgentExecutionStatus.FAILED || status === AgentExecutionStatus.CANCELLED) {
    const t2 = status === AgentExecutionStatus.CANCELLED ? "Cancelled" : "Failed";
    let t3;
    if ($[6] !== className) {
      t3 = cx("shrink-0 text-status-error-text", className);
      $[6] = className;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== t2 || $[9] !== t3) {
      t4 = <FiXCircle aria-label={t2} className={t3} />;
      $[8] = t2;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  if (status === AgentExecutionStatus.RUNNING) {
    let t2;
    if ($[11] !== className) {
      t2 = cx("inline-flex shrink-0 text-status-info-text", styles.running, className);
      $[11] = className;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== Icon) {
      t3 = <Icon aria-hidden="true" className="size-full" />;
      $[13] = Icon;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] !== t2 || $[16] !== t3) {
      t4 = <span aria-label="Running" className={t2}>{t3}</span>;
      $[15] = t2;
      $[16] = t3;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    return t4;
  }
  let t2;
  if ($[18] !== className) {
    t2 = cx("inline-flex shrink-0 text-foreground-muted", className);
    $[18] = className;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  let t3;
  if ($[20] !== Icon) {
    t3 = <Icon aria-hidden="true" className="size-full" />;
    $[20] = Icon;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== t2 || $[23] !== t3) {
    t4 = <span aria-label="Pending" className={t2}>{t3}</span>;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  return t4;
}
function AgentActivityLogDetail(t0) {
  const $ = _c(17);
  const {
    detail
  } = t0;
  const resolvedTheme = useThemeStore(_temp2);
  const prismTheme = resolvedTheme === "dark" ? themes.vsDark : themes.vsLight;
  let t1;
  if ($[0] !== detail.language) {
    t1 = getDetailIcon(detail.language);
    $[0] = detail.language;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const DetailIcon = t1;
  const language = detail.language ?? "text";
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = accordionChevronClassName("size-3");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== DetailIcon) {
    t3 = <DetailIcon className="h-3 w-3 shrink-0" />;
    $[3] = DetailIcon;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== detail.label) {
    t4 = <span>{detail.label}</span>;
    $[5] = detail.label;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <AccordionTrigger icon={FiChevronRight} iconPosition="start" iconClassName={t2} className="w-fit flex-none justify-start gap-1.5 rounded px-1.5 py-0.5 text-caption font-normal leading-normal text-foreground-muted hover:bg-hover hover:text-foreground data-[state=closed]:rounded-none">{t3}{t4}</AccordionTrigger>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== detail.content || $[11] !== language || $[12] !== prismTheme) {
    t6 = <AccordionContent className="pb-0 text-inherit"><div className="mt-1 max-h-64 overflow-auto rounded border border-border text-caption scrollbar-thin">{language === "text" ? <pre className="bg-background-secondary p-2 text-foreground-secondary"><code>{detail.content}</code></pre> : <Highlight theme={prismTheme} code={detail.content} language={language}>{_temp3}</Highlight>}</div></AccordionContent>;
    $[10] = detail.content;
    $[11] = language;
    $[12] = prismTheme;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <div className="ml-5 mt-0.5"><Accordion type="single" collapsible={true}><AccordionItem value={DETAIL_ACCORDION_VALUE} className={accordionItemClassName}>{t5}{t6}</AccordionItem></Accordion></div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function _temp3(t0) {
  const {
    className,
    style,
    tokens,
    getLineProps,
    getTokenProps
  } = t0;
  return <pre className={cx(className, "max-h-32 overflow-y-auto p-2 leading-relaxed")} style={{
    ...style,
    margin: 0,
    borderRadius: 0
  }}>{tokens.map((line, i) => <div key={i} {...getLineProps({
      line
    })}><span className="mr-4 inline-block min-w-6 select-none text-right opacity-30">{i + 1}</span>{line.map((token, key) => <span key={key} {...getTokenProps({
        token
      })} />)}</div>)}</pre>;
}
function _temp2(s) {
  return s.resolvedTheme;
}
interface AgentActivitySummaryProps {
  item: AgentActivityLogItem;
  className?: string;
  children?: React.ReactNode;
}
function activitySummaryClasses(item: AgentActivityLogItem, className?: string) {
  return cx(activitySummaryClassName, styles.row, (item.status === AgentExecutionStatus.RUNNING || item.status === AgentExecutionStatus.PENDING) && "text-foreground", item.status === AgentExecutionStatus.COMPLETED && "text-foreground-secondary", (item.status === AgentExecutionStatus.FAILED || item.status === AgentExecutionStatus.CANCELLED) && "text-status-error-text", item.kind === AgentActivityKind.THINKING && "italic", className);
}
function AgentActivitySummaryContent(t0) {
  const $ = _c(16);
  const {
    item,
    children
  } = t0;
  let t1;
  if ($[0] !== item.kind || $[1] !== item.status) {
    t1 = <AgentActivityLogStatusIcon status={item.status} kind={item.kind} className="size-3.5" />;
    $[0] = item.kind;
    $[1] = item.status;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = item.kind === AgentActivityKind.PLAN ? "whitespace-normal" : "truncate";
  let t3;
  if ($[3] !== t2) {
    t3 = cx("min-w-0 flex-1", t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children || $[6] !== item.label || $[7] !== t3) {
    t4 = <span className={t3}>{item.label}{children}</span>;
    $[5] = children;
    $[6] = item.label;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== item.elapsedMs || $[10] !== item.status) {
    t5 = item.status === AgentExecutionStatus.COMPLETED && item.elapsedMs != null && item.elapsedMs > 0 && <span className="shrink-0 text-caption text-foreground-muted tabular-nums">{(item.elapsedMs / 1000).toFixed(1)}s</span>;
    $[9] = item.elapsedMs;
    $[10] = item.status;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t1 || $[13] !== t4 || $[14] !== t5) {
    t6 = <>{t1}{t4}{t5}</>;
    $[12] = t1;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function AgentActivitySummaryTrigger(t0) {
  const $ = _c(16);
  let children;
  let className;
  let item;
  let props;
  if ($[0] !== t0) {
    ({
      item,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = item;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    item = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = accordionChevronClassName("size-3.5");
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== className || $[7] !== item) {
    t2 = cx(activitySummaryClasses(item, className), "rounded-md hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none");
    $[6] = className;
    $[7] = item;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children || $[10] !== item) {
    t3 = <AgentActivitySummaryContent item={item}>{children}</AgentActivitySummaryContent>;
    $[9] = children;
    $[10] = item;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== props || $[13] !== t2 || $[14] !== t3) {
    t4 = <AccordionTrigger {...props} icon={FiChevronRight} iconPosition="start" iconClassName={t1} className={t2}>{t3}</AccordionTrigger>;
    $[12] = props;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
function AgentActivitySummaryStatic(t0) {
  const $ = _c(10);
  const {
    item,
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== item) {
    t1 = activitySummaryClasses(item, className);
    $[0] = className;
    $[1] = item;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span aria-hidden="true" className="size-3.5 shrink-0" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== item) {
    t3 = <AgentActivitySummaryContent item={item}>{children}</AgentActivitySummaryContent>;
    $[4] = children;
    $[5] = item;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
const AgentActivitySummary = {
  Trigger: AgentActivitySummaryTrigger,
  Static: AgentActivitySummaryStatic
};
function AgentActivityLogRow(t0) {
  const $ = _c(23);
  const {
    item,
    compact: t1,
    phase
  } = t0;
  const compact = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== item.detail || $[1] !== item.details) {
    t2 = item.details ?? (item.detail ? [item.detail] : []);
    $[0] = item.detail;
    $[1] = item.details;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const details = t2;
  const resolvedPhase = phase ?? (item.status === AgentExecutionStatus.RUNNING || item.status === AgentExecutionStatus.PENDING ? "current" : "past");
  const {
    open,
    onOpenChange
  } = useActivityDisclosure(resolvedPhase);
  const sizeClassName = compact ? "text-caption" : "text-body";
  if (details.length === 0) {
    let t3;
    if ($[3] !== item || $[4] !== sizeClassName) {
      t3 = <AgentActivitySummary.Static item={item} className={sizeClassName} />;
      $[3] = item;
      $[4] = sizeClassName;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  const t3 = open ? DETAIL_ACCORDION_VALUE : "";
  let t4;
  if ($[6] !== onOpenChange) {
    t4 = value => onOpenChange(value === DETAIL_ACCORDION_VALUE);
    $[6] = onOpenChange;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cx(accordionItemClassName, styles.rowDisclosure);
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== item || $[10] !== sizeClassName) {
    t6 = <AgentActivitySummary.Trigger item={item} className={sizeClassName} />;
    $[9] = item;
    $[10] = sizeClassName;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== details) {
    t7 = details.map(_temp4);
    $[12] = details;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <AccordionContent className="pb-0 text-inherit">{t7}</AccordionContent>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t6 || $[17] !== t8) {
    t9 = <AccordionItem value={DETAIL_ACCORDION_VALUE} className={t5}>{t6}{t8}</AccordionItem>;
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t3 || $[20] !== t4 || $[21] !== t9) {
    t10 = <Accordion type="single" collapsible={true} value={t3} onValueChange={t4}>{t9}</Accordion>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}

// Tool settlement is not a boundary: more calls can still join this group.
// Retire once later activity appears, then allow deliberate history inspection.
function _temp4(detail) {
  return <AgentActivityLogDetail key={detail.label} detail={detail} />;
}
function useActivityDisclosure(phase) {
  const $ = _c(5);
  const [disclosure, setDisclosure] = useState(null);
  const manualOpen = disclosure?.phase === phase ? disclosure.open : undefined;
  const t0 = manualOpen ?? phase === "current";
  let t1;
  if ($[0] !== phase) {
    t1 = open => setDisclosure({
      open,
      phase
    });
    $[0] = phase;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      open: t0,
      onOpenChange: t1
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// Projected results may disappear, but a crossed boundary stays crossed.
function useActivityRetirement(hasFollowingResult) {
  const [retired, setRetired] = useState(hasFollowingResult);
  if (hasFollowingResult && !retired) {
    setRetired(true);
  }
  return retired || hasFollowingResult ? "past" : "current";
}
function AgentActivityHeaderSlot(t0) {
  const $ = _c(8);
  const {
    state,
    children
  } = t0;
  const shown = state === "shown";
  const t1 = !shown;
  const t2 = !shown;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cx(accordionItemClassName, styles.headerSurface);
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const t4 = shown ? children : null;
  let t5;
  if ($[1] !== t4) {
    t5 = <div><div className={t3}>{t4}</div></div>;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== shown || $[4] !== t1 || $[5] !== t2 || $[6] !== t5) {
    t6 = <div className={styles.headerSlot} data-visible={shown} aria-hidden={t1} inert={t2}>{t5}</div>;
    $[3] = shown;
    $[4] = t1;
    $[5] = t2;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}

/** Tool logs only: the visible result ending this stretch stays outside. */
export function AgentActivityStretch(t0) {
  const $ = _c(24);
  const {
    phase: incomingPhase,
    count,
    summary,
    children
  } = t0;
  const phase = useActivityRetirement(incomingPhase === "past");
  const {
    open,
    onOpenChange
  } = useActivityDisclosure(phase);
  const retired = phase === "past";
  if (count === 0) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const t1 = !retired || open;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cx("mt-0", styles.stretch);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = retired ? "shown" : "hidden";
  let t4;
  if ($[3] !== summary) {
    t4 = <span className="truncate">{summary}</span>;
    $[3] = summary;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== summary || $[6] !== t4) {
    t5 = <AccordionTrigger icon={FiChevronRight} iconPosition="end" iconClassName="size-3 shrink-0 text-inherit group-data-[state=open]:rotate-90 motion-reduce:transition-none" className="min-w-0 max-w-full flex-none justify-start gap-1 rounded py-1 text-caption text-foreground-muted hover:text-foreground-secondary motion-reduce:transition-none" aria-label={summary} title={summary}>{t4}</AccordionTrigger>;
    $[5] = summary;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t5) {
    t6 = <AgentActivityHeaderSlot state={t3}>{t5}</AgentActivityHeaderSlot>;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = retired ? "ml-1.5 border-l border-border/50 pl-4" : "ml-0 pl-0";
  let t8;
  if ($[11] !== t7) {
    t8 = cx(styles.list, t7);
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== children || $[14] !== t8) {
    t9 = <AccordionContent className="pb-0 text-inherit"><div className={t8}>{children}</div></AccordionContent>;
    $[13] = children;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== onOpenChange || $[17] !== t1 || $[18] !== t6 || $[19] !== t9) {
    t10 = <AgentActivityLogRoot open={t1} onOpenChange={onOpenChange} className={t2}>{t6}{t9}</AgentActivityLogRoot>;
    $[16] = onOpenChange;
    $[17] = t1;
    $[18] = t6;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== phase || $[22] !== t10) {
    t11 = <AgentActivityStretchContext value={phase}>{t10}</AgentActivityStretchContext>;
    $[21] = phase;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}
export function AgentActivityGroup(t0) {
  const $ = _c(27);
  const {
    label,
    status,
    items,
    className,
    hasFollowingActivity: t1
  } = t0;
  const hasFollowingActivity = t1 === undefined ? false : t1;
  const stretchPhase = use(AgentActivityStretchContext);
  const phase = useActivityRetirement(hasFollowingActivity || stretchPhase === "past");
  const {
    open,
    onOpenChange
  } = useActivityDisclosure(phase);
  const listRef = useRef(null);
  let t2;
  let t3;
  if ($[0] !== open) {
    t2 = () => {
      if (!open) {
        return;
      }
      const list = listRef.current;
      if (list) {
        list.scrollTop = list.scrollHeight;
      }
    };
    t3 = [open];
    $[0] = open;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  if (items.length === 0) {
    return null;
  }
  const grouped = stretchPhase === "past" || items.length > 1;
  const t4 = !grouped || open;
  const t5 = grouped ? "shown" : "hidden";
  let t6;
  if ($[3] !== items || $[4] !== label || $[5] !== status) {
    t6 = <AgentActivityLogHeader items={items} status={status} label={label} />;
    $[3] = items;
    $[4] = label;
    $[5] = status;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5 || $[8] !== t6) {
    t7 = <AgentActivityHeaderSlot state={t5}>{t6}</AgentActivityHeaderSlot>;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = !grouped || open;
  let t9;
  if ($[10] !== grouped || $[11] !== items || $[12] !== phase) {
    let t10;
    if ($[14] !== grouped || $[15] !== phase) {
      t10 = item => <AgentActivityLogRow key={item.id} item={item} compact={!grouped && phase === "past"} phase={phase} />;
      $[14] = grouped;
      $[15] = phase;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    t9 = items.map(t10);
    $[10] = grouped;
    $[11] = items;
    $[12] = phase;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[17] !== grouped || $[18] !== t8 || $[19] !== t9) {
    t10 = <AgentActivityLogList listRef={listRef} active={t8} indented={grouped}>{t9}</AgentActivityLogList>;
    $[17] = grouped;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== className || $[22] !== onOpenChange || $[23] !== t10 || $[24] !== t4 || $[25] !== t7) {
    t11 = <AgentActivityLogRoot className={className} open={t4} onOpenChange={onOpenChange}>{t7}{t10}</AgentActivityLogRoot>;
    $[21] = className;
    $[22] = onOpenChange;
    $[23] = t10;
    $[24] = t4;
    $[25] = t7;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}
export function AgentActivityTimeline(t0) {
  const $ = _c(17);
  const {
    status,
    items,
    className
  } = t0;
  const phase = status === AgentExecutionStatus.RUNNING || status === AgentExecutionStatus.PENDING ? "current" : "past";
  const {
    open,
    onOpenChange
  } = useActivityDisclosure(phase);
  const listRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== open) {
    t1 = () => {
      if (!open) {
        return;
      }
      const list = listRef.current;
      if (list) {
        list.scrollTop = list.scrollHeight;
      }
    };
    t2 = [open];
    $[0] = open;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (items.length === 0) {
    return null;
  }
  let t3;
  if ($[3] !== items || $[4] !== status) {
    t3 = <AgentActivityLogHeader items={items} status={status} />;
    $[3] = items;
    $[4] = status;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== items) {
    t4 = items.map(_temp5);
    $[6] = items;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== open || $[9] !== t4) {
    t5 = <AgentActivityLogList listRef={listRef} active={open}>{t4}</AgentActivityLogList>;
    $[8] = open;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== className || $[12] !== onOpenChange || $[13] !== open || $[14] !== t3 || $[15] !== t5) {
    t6 = <AgentActivityLogRoot className={className} open={open} onOpenChange={onOpenChange}>{t3}{t5}</AgentActivityLogRoot>;
    $[11] = className;
    $[12] = onOpenChange;
    $[13] = open;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function _temp5(item) {
  return <AgentActivityLogRow key={item.id} item={item} />;
}
function resolveGroupStatus(items: AgentActivityLogItem[]): AgentActivityStatus {
  if (items.some(item => item.status === AgentExecutionStatus.RUNNING)) {
    return AgentExecutionStatus.RUNNING;
  }
  if (items.some(item => item.status === AgentExecutionStatus.PENDING)) {
    return AgentExecutionStatus.PENDING;
  }
  if (items.every(item => item.status === AgentExecutionStatus.FAILED)) {
    return AgentExecutionStatus.FAILED;
  }
  if (items.some(item => item.status === AgentExecutionStatus.CANCELLED)) {
    return AgentExecutionStatus.CANCELLED;
  }
  return AgentExecutionStatus.COMPLETED;
}
function AgentActivityLogComponent(t0) {
  const $ = _c(18);
  const {
    items,
    defaultCollapsed: t1,
    className
  } = t0;
  const defaultCollapsed = t1 === undefined ? true : t1;
  const [open, setOpen] = useState(!defaultCollapsed);
  const listRef = useRef(null);
  let t2;
  let t3;
  if ($[0] !== open) {
    t2 = () => {
      if (!open) {
        return;
      }
      const list = listRef.current;
      if (!list) {
        return;
      }
      list.scrollTop = list.scrollHeight;
    };
    t3 = [open];
    $[0] = open;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  if (items.length === 0) {
    return null;
  }
  let t4;
  if ($[3] !== items) {
    t4 = resolveGroupStatus(items);
    $[3] = items;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const status = t4;
  let t5;
  if ($[5] !== items || $[6] !== status) {
    t5 = <AgentActivityLogHeader items={items} status={status} />;
    $[5] = items;
    $[6] = status;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== items) {
    t6 = items.map(_temp6);
    $[8] = items;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== open || $[11] !== t6) {
    t7 = <AgentActivityLogList listRef={listRef} active={open}>{t6}</AgentActivityLogList>;
    $[10] = open;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== className || $[14] !== open || $[15] !== t5 || $[16] !== t7) {
    t8 = <AgentActivityLogRoot className={className} open={open} onOpenChange={setOpen}>{t5}{t7}</AgentActivityLogRoot>;
    $[13] = className;
    $[14] = open;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
function _temp6(item) {
  return <AgentActivityLogRow key={item.id} item={item} />;
}
export const AgentActivityLog = Object.assign(AgentActivityLogComponent, {
  Root: AgentActivityLogRoot,
  Header: AgentActivityLogHeader,
  List: AgentActivityLogList,
  Row: AgentActivityLogRow,
  Detail: AgentActivityLogDetail
});